import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { Button, ButtonStyle, Checkbox, Form, FormField, Spinner } from "@prequel/shared-components";

import { useAllModels } from "@/api/queries/models";
import { useCreateProduct, useProduct, useUpdateProduct } from "@/api/queries/products";
import { computeProductChangedFields, Product } from "@/types";

const initialProductState: Product = {
  product_name: "",
  description: "",
  models: [],
};

const ProductForm = () => {
  const navigate = useNavigate();
  const { productID } = useParams<{ productID: string }>();
  const productQueryResult = useProduct(productID);
  const isEditing = !!productID;

  const productToEdit = productQueryResult.data;

  const { data: models } = useAllModels();
  const availableModels = models?.map(({ model_name }) => model_name) ?? [];

  const createProductMutation = useCreateProduct();
  const updateProductMutation = useUpdateProduct();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [product, setProduct] = useState<Product>(initialProductState);
  const [productsError, setProductsError] = useState<string>();

  const isProcessing = createProductMutation.isPending || updateProductMutation.isPending;

  useEffect(() => {
    if (productToEdit) {
      setProduct({
        ...productToEdit,
      });
    }
  }, [productToEdit]);

  const setField = (key: keyof Product, value: string | string[]) => {
    setProduct((oldProduct) => ({
      ...oldProduct,
      [key]: value,
    }));
  };

  const updateModels = (isEnabled: boolean, modelName: string) => {
    const updatedModels = isEnabled ? [...product.models, modelName] : product.models.filter((p) => p !== modelName);
    setProductsError(undefined);
    setField("models", updatedModels);
  };

  const onSubmit = () => {
    if (isEditing && productToEdit) {
      const changed = computeProductChangedFields(productToEdit, product);
      updateProductMutation.mutate({
        id: productToEdit.id,
        fields: changed,
        redirectPath: `/models/products/${productToEdit.id}`,
      });
    } else {
      createProductMutation.mutate({
        product,
        redirectPath: "/models/products",
      });
    }
  };

  return (
    <Form className="space-y-4" onSubmit={onSubmit} submitButtonRef={buttonRef}>
      <FormField
        label="Name"
        id="name"
        type="text"
        subtext="Descriptive name for this Product. This will only be visible internally and is only used as a reference."
        value={product.product_name}
        onChangeHandler={(value: string) => setField("product_name", value)}
        disabled={isProcessing}
        required
      />
      <FormField
        label="Description"
        id="description"
        type="text"
        subtext="A brief description of this Product. This will only be visible internally."
        value={product.description}
        onChangeHandler={(value: string) => setField("description", value)}
        disabled={isProcessing}
        required
      />
      {availableModels && (
        <>
          <label className="block text-sm font-medium text-gray-700">Select what models the product will contain</label>
          {availableModels.map((m) => (
            <Checkbox
              key={m}
              id={m}
              label={m}
              checked={product.models.includes(m)}
              setChecked={(isChecked: boolean) => updateModels(isChecked, m)}
              disabled={isProcessing}
            />
          ))}
          {productsError && <p className="mt-1 text-xs font-medium text-red-600">{productsError}</p>}
        </>
      )}
      <div className="mt-8 flex justify-end">
        {isEditing && (
          <Button
            className="mr-3"
            type={ButtonStyle.TERTIARY}
            disabled={isProcessing}
            onClick={() => navigate("/models/products")}
            text="Cancel"
          />
        )}
        <Button
          ref={buttonRef}
          type={ButtonStyle.PRIMARY}
          disabled={isProcessing}
          text={
            isProcessing ? (
              <div className="flex">
                <Spinner.Inline className="mr-3 text-white" /> Saving Product...
              </div>
            ) : (
              "Save Product"
            )
          }
          submit
        />
      </div>
    </Form>
  );
};

export default ProductForm;
